import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useRange } from '../../range';
import { Band, Card } from '../../components/Card';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import KpiTile from '../../components/KpiTile';
import LineTrend from '../../components/LineTrend';
import PageHeader from '../../components/PageHeader';
import { formatCompactNumber, formatDateTime, formatMs, formatPct } from '../../format';
import useSort, { applySort } from '../../hooks/useSort';

/**
 * AI › LLM Usage — every model call MAHI makes, across every service (chat,
 * rationale, enrichment, vision, nightly source discovery, embeddings,
 * safety moderation), from one audit trail (backend/services/llm/audit.py).
 *
 * Token counts here are UNCACHED — what a cache-heavy chat prompt actually
 * gets billed for, not the raw input-token count a vendor reports (which
 * includes anything served from cache). See services/llm/types.py::Usage.
 */
const SERIES = { calls: '#2a78d6', tokens: '#1baf7a', errors: '#eb6834' };

function yesterdayUtc() {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export default function LlmUsagePage() {
  const range = useRange();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/llm-usage/overview', { params: { days: range.days } });
      setData(data);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load LLM usage'));
    } finally {
      setLoading(false);
    }
  }, [range.days]);

  useEffect(() => { load(); }, [load]);

  const overall = data?.overall || {};
  const trend = data?.trend || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const byPurposeModel = useMemo(() => data?.by_purpose_model || [], [data]);
  const noData = !loading && overall.calls === 0 && trend.length === 0;

  const usageSeries = [
    { key: 'calls', label: 'Calls', color: SERIES.calls, values: trend.map((d) => ({ x: d.date, y: d.calls })) },
    { key: 'errors', label: 'Errors', color: SERIES.errors, values: trend.map((d) => ({ x: d.date, y: d.errors })) },
  ];
  const tokenSeries = [
    {
      key: 'tokens', label: 'Uncached tokens', color: SERIES.tokens,
      values: trend.map((d) => ({ x: d.date, y: d.uncached_total_tokens })),
    },
  ];

  const purposeColumns = [
    { key: 'purpose', label: 'Purpose', render: (r) => <span className="font-medium text-ink">{r.purpose}</span> },
    { key: 'model', label: 'Model', render: (r) => <span className="font-mono text-xs text-ink-secondary">{r.model || '-'}</span> },
    { key: 'calls', label: 'Calls', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.calls) },
    { key: 'error_rate', label: 'Error rate', align: 'right', sortable: true, render: (r) => formatPct(r.error_rate * 100, 1) },
    { key: 'uncached_input_tokens', label: 'Input (uncached)', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.uncached_input_tokens) },
    { key: 'output_tokens', label: 'Output', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.output_tokens) },
    { key: 'latency_ms_p90', label: 'Latency p90', align: 'right', sortable: true, render: (r) => formatMs(r.latency_ms_p90) },
  ];

  const { sort, onSort } = useSort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sortedPurposes = useMemo(() => applySort(byPurposeModel, sort, purposeColumns), [byPurposeModel, sort]);

  // ─── Drill-down: the actual calls ─────────────────────────────────────────
  const [callsDate, setCallsDate] = useState(yesterdayUtc());
  const [callsPurpose, setCallsPurpose] = useState('');
  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);

  const loadCalls = useCallback(async () => {
    setCallsLoading(true);
    try {
      const params = { date: callsDate || undefined, purpose: callsPurpose || undefined };
      const { data } = await adminApi.get('/llm-usage/calls', { params });
      setCalls(data.items || []);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load calls'));
    } finally {
      setCallsLoading(false);
    }
  }, [callsDate, callsPurpose]);

  useEffect(() => { loadCalls(); }, [loadCalls]);

  const callColumns = [
    { key: 'ts', label: 'Time', render: (r) => <span className="whitespace-nowrap text-xs">{formatDateTime(r.ts)}</span> },
    { key: 'purpose', label: 'Purpose', render: (r) => r.purpose },
    { key: 'use_case', label: 'Use case', render: (r) => <span className="text-xs text-ink-secondary">{r.use_case}</span> },
    { key: 'model', label: 'Model', render: (r) => <span className="font-mono text-xs">{r.model}</span> },
    {
      key: 'status',
      label: 'Status',
      render: (r) => (
        <span className={r.status === 'error' ? 'text-red-600' : 'text-ink-secondary'}>
          {r.status}{r.error ? ` - ${r.error}` : ''}
        </span>
      ),
    },
    { key: 'user_id', label: 'User', render: (r) => <span className="font-mono text-xs">{r.user_id || '-'}</span> },
    { key: 'uncached_input_tokens', label: 'Input (uncached)', align: 'right', render: (r) => formatCompactNumber(r.uncached_input_tokens) },
    { key: 'output_tokens', label: 'Output', align: 'right', render: (r) => formatCompactNumber(r.output_tokens) },
    { key: 'latency_ms', label: 'Latency', align: 'right', render: (r) => formatMs(r.latency_ms) },
  ];

  const purposeOptions = useMemo(
    () => Array.from(new Set(byPurposeModel.map((b) => b.purpose))).sort(),
    [byPurposeModel],
  );

  return (
    <div>
      <PageHeader
        title="LLM Usage"
        hint={`Every model call MAHI makes, across chat, rationale, enrichment, vision, discovery and embeddings, ${range.label.toLowerCase()}.`}
      />

      {loading && !data && <Card><p className="text-sm text-ink-muted">Loading…</p></Card>}

      {noData && (
        <EmptyState
          tone="gap"
          title="No LLM usage rollup for this window"
          detail="These numbers come from the nightly rollup of services/llm/audit.py's per-call rows. Nothing has landed for these dates yet."
        />
      )}

      {data && !noData && (
        <>
          <Band title="Usage">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiTile label="Calls" value={formatCompactNumber(overall.calls)} />
              <KpiTile label="Error rate" value={formatPct((overall.error_rate || 0) * 100, 1)} goodWhen="down" />
              <KpiTile
                label="Uncached input tokens"
                value={formatCompactNumber(overall.uncached_input_tokens)}
                caption="What actually gets billed at the full input rate"
              />
              <KpiTile label="Output tokens" value={formatCompactNumber(overall.output_tokens)} />
            </div>
          </Band>

          <Band title="Calls & errors">
            <LineTrend series={usageSeries} height={160} />
          </Band>

          <Band title="Tokens (uncached)">
            <LineTrend series={tokenSeries} height={160} />
          </Band>

          <Band title="Speed">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <KpiTile label="Latency p50" value={formatMs(overall.latency_ms_p50)} caption="Worst day in range" />
              <KpiTile label="Latency p90" value={formatMs(overall.latency_ms_p90)} caption="Worst day in range" />
              <KpiTile label="Latency p99" value={formatMs(overall.latency_ms_p99)} caption="Worst day in range" />
            </div>
          </Band>

          <Band
            title="By purpose and model"
            actions={<span className="text-[11px] text-ink-muted">Summed across the window; latency is the worst day's percentile.</span>}
          >
            <DataTable
              columns={purposeColumns}
              rows={sortedPurposes}
              rowKey={(r) => `${r.purpose}#${r.model}`}
              dense
              sort={sort}
              onSort={onSort}
              empty={<EmptyState title="No calls recorded in this window" />}
            />
          </Band>

          <Band
            title="Recent calls"
            actions={
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={callsDate}
                  onChange={(e) => setCallsDate(e.target.value)}
                  className="rounded-md border border-hairline px-2 py-1 text-xs"
                />
                <select
                  value={callsPurpose}
                  onChange={(e) => setCallsPurpose(e.target.value)}
                  className="rounded-md border border-hairline px-2 py-1 text-xs"
                >
                  <option value="">All purposes</option>
                  {purposeOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            }
          >
            <p className="mb-2 text-xs leading-relaxed text-ink-secondary">
              Who called which model, for what - the per-call detail behind the totals above.
            </p>
            <DataTable
              columns={callColumns}
              rows={calls}
              rowKey={(r) => r.call_id}
              dense
              empty={
                <EmptyState
                  title={callsLoading ? 'Loading…' : 'No calls on this day'}
                  detail="Pick a different date, or filter by purpose instead."
                />
              }
            />
          </Band>
        </>
      )}
    </div>
  );
}
